/**
 * Disputes Management Page
 * Manage and review all disputes across the platform
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableHead,
  Chip,
  Link,
  SelectChangeEvent,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { MainLayout } from '../../components/layout/MainLayout';
import { LoadingSpinner, EmptyState, TablePagination, CopyableText } from '../../components/common';
import { Card, CardContent, TableCell, TableHeaderCell, TableRow, TableContainer, SearchBox } from '../../components/ui';
import { DisputeService } from '../../services/dispute/DisputeService';
import { formatDateTime } from '../../utils/dateUtils';
import type {
  Dispute,
  DisputeStatus,
  PaginatedDisputes,
} from '../../types/dispute';

type StatusFilter = DisputeStatus | 'all';

const statusFilters: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'underreview', label: 'Under Review' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'rejected', label: 'Rejected' },
];

const statusLabels: Record<DisputeStatus, string> = {
  underreview: 'Under Review',
  resolved: 'Resolved',
  rejected: 'Rejected',
};

const getStatusColor = (status: DisputeStatus, theme: any): string => {
  switch (status) {
    case 'underreview':
      return theme.palette.warning.main;
    case 'resolved':
      return theme.palette.success.main;
    case 'rejected':
      return theme.palette.error.main;
    default:
      return theme.palette.text.secondary;
  }
};

const SEARCH_DEBOUNCE_MS = 400;

const DisputesManagementPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<Set<DisputeStatus>>(new Set());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalResults, setTotalResults] = useState(0);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchValue]);

  const fetchDisputes = useCallback(async () => {
    try {
      setIsLoading(true);
      const result: PaginatedDisputes = await DisputeService.getDisputes(
        page,
        pageSize,
        {
          status: selectedStatuses.size > 0 ? Array.from(selectedStatuses) : 'all',
          search: debouncedSearchValue || undefined
        }
      );
      setDisputes(result.data);
      setTotalResults(result.total);
    } catch (error) {
      console.error('Failed to fetch disputes:', error);
      setDisputes([]);
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, selectedStatuses, debouncedSearchValue]);

  useEffect(() => {
    fetchDisputes();
  }, [fetchDisputes]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    setPage(1);
  };

  const handleStatusToggle = (status: DisputeStatus) => {
    const newSelected = new Set(selectedStatuses);
    if (newSelected.has(status)) {
      newSelected.delete(status);
    } else {
      newSelected.add(status);
    }
    setSelectedStatuses(newSelected);
    setPage(1);
  };



  const handleViewDetails = (dispute: Dispute) => {
    navigate(`/disputes/${dispute.id}`);
  };

  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    setPageSize(Number(event.target.value));
    setPage(1);
  };

  return (
    <MainLayout>
      {/* Page Header */}
      <Box sx={{ marginBottom: theme.spacing(3) }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            marginBottom: theme.spacing(1),
          }}
        >
          Disputes Management
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: theme.palette.text.secondary }}
        >
          Review and resolve disputes between Assets and Ghosts.
        </Typography>
      </Box>

      {/* Search and Filters */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: theme.spacing(3),
          flexWrap: 'wrap',
          gap: theme.spacing(2),
        }}
      >
        {/* Search Box */}
        <SearchBox
          placeholder="Search by Mission Code, Title or Call Sign..."
          value={searchValue}
          onChange={handleSearch}
          containerSx={{ flex: 1, minWidth: 300 }}
        />

        {/* Status Filters (Multi-select with Chips) */}
        <Box sx={{ width: '100%', marginBottom: theme.spacing(1) }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              marginBottom: theme.spacing(1.5),
              color: theme.palette.text.primary,
              fontSize: '0.875rem',
            }}
          >
            Filter by Status:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1) }}>
            {statusFilters.filter(f => f.value !== 'all').map((option) => {
              const isSelected = selectedStatuses.has(option.value as DisputeStatus);
              return (
                <Chip
                  key={option.value}
                  label={option.label}
                  onClick={() => handleStatusToggle(option.value as DisputeStatus)}
                  onDelete={isSelected ? (e) => {
                    e.stopPropagation();
                    handleStatusToggle(option.value as DisputeStatus);
                  } : undefined}
                  clickable
                  sx={{
                    backgroundColor: isSelected
                      ? theme.palette.primary.main
                      : theme.palette.background.paper,
                    color: isSelected
                      ? theme.palette.primary.contrastText
                      : theme.palette.text.primary,
                    fontWeight: 500,
                    borderRadius: theme.shapes.borderRadius.lg,
                    border: `1px solid ${isSelected
                      ? theme.palette.primary.main
                      : (theme as any).customColors.tableBorder
                      }`,
                    '&:hover': {
                      backgroundColor: isSelected
                        ? theme.palette.primary.dark
                        : (theme as any).customColors.tableRowHover,
                    },
                  }}
                />
              );
            })}
          </Box>
        </Box>

      </Box>

      {/* Disputes Table */}
      <Card>
        <CardContent noPadding>
          {isLoading ? (
            <Box sx={{ padding: theme.spacing(6), display: 'flex', justifyContent: 'center' }}>
              <LoadingSpinner />
            </Box>
          ) : disputes.length === 0 ? (
            <Box sx={{ padding: theme.spacing(4) }}>
              <EmptyState
                title="No Disputes Found"
                message="There are no disputes matching your criteria."
              />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table sx={{ minWidth: 1200 }}>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Mission Code</TableHeaderCell>
                      <TableHeaderCell>Mission Title</TableHeaderCell>
                      <TableHeaderCell>Status</TableHeaderCell>
                      <TableHeaderCell>Asset</TableHeaderCell>
                      <TableHeaderCell>Ghost</TableHeaderCell>
                      <TableHeaderCell>Date Opened</TableHeaderCell>
                      <TableHeaderCell>Last Update</TableHeaderCell>
                      <TableHeaderCell>Action</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {disputes.map((dispute) => {
                      const client = dispute.parties.find((p) => p.type === 'client');
                      const agent = dispute.parties.find((p) => p.type === 'agent');
                      return (
                        <TableRow key={dispute.id}>
                          <TableCell>
                            <CopyableText
                              text={dispute.missionCode || dispute.missionId}
                              onTextClick={() => navigate(`/missions/${dispute.missionId}`)}
                              sx={{ color: theme.palette.primary.main, fontWeight: 500 }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: theme.palette.text.primary }}>
                            {dispute.missionTitle || 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Box
                              component="span"
                              sx={{
                                display: 'inline-block',
                                padding: theme.spacing(0.5, 1.5),
                                borderRadius: theme.shapes.borderRadius.lg,
                                backgroundColor: getStatusColor(dispute.status, theme),
                                color: theme.palette.common.white,
                                fontWeight: 500,
                                fontSize: theme.typography.caption.fontSize,
                              }}
                            >
                              {statusLabels[dispute.status]}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 500 }}>
                                {client?.name || 'N/A'}
                              </Typography>
                              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                {client?.email}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 500 }}>
                                {agent?.name || 'N/A'}
                              </Typography>
                              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                {agent?.email}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ color: theme.palette.text.secondary }}>
                            {formatDateTime(dispute.dateOpened)}
                          </TableCell>
                          <TableCell sx={{ color: theme.palette.text.secondary }}>
                            {formatDateTime(dispute.lastUpdate)}
                          </TableCell>
                          <TableCell>
                            <Link
                              component="button"
                              variant="body2"
                              onClick={() => handleViewDetails(dispute)}
                              sx={{
                                color: theme.palette.primary.main,
                                cursor: 'pointer',
                                textDecoration: 'none',
                                fontSize: '0.875rem',
                                fontWeight: 400,
                                '&:hover': {
                                  textDecoration: 'underline',
                                },
                              }}
                            >
                              View
                            </Link>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <TablePagination
                page={page}
                pageSize={pageSize}
                totalItems={totalResults}
                onPageChange={setPage}
                onPageSizeChange={handlePageSizeChange}
              />
            </>
          )}
        </CardContent>
      </Card>
    </MainLayout>
  );
};

export default DisputesManagementPage;
